// family-discourse-core/app/assets/javascripts/discourse/lib/discourse-message-bus.js
class DiscourseMessageBus {
  constructor() {
    this.messageBusUrl = null;
    this.polling = false;
    this.subscriptions = new Map();
    this.lastMessageId = 0;
    this.xhr = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.pollInterval = 30000;
    this.userApiKey = null;
    this.userApiClientId = null;
  }

  // Inicializar com URL do Message Bus e API Key
  initialize(messageBusUrl, userApiKey, userApiClientId = null) {
    // Configura URL
    if (!messageBusUrl) {
      const metaTag = document.querySelector('meta[name="discourse/config/environment"]');
      if (metaTag) {
        try {
          const config = JSON.parse(decodeURIComponent(metaTag.content));
          this.messageBusUrl = config.baseURL || '/';
        } catch (e) {
          this.messageBusUrl = '/';
        }
      } else {
        this.messageBusUrl = '/';
      }
    } else {
      this.messageBusUrl = messageBusUrl.replace(/\/$/, '') + '/';
    }

    // Configura API Key (deve ser passada via initialize(), não via variável de ambiente)
    this.userApiKey = userApiKey || '';
    this.userApiClientId = userApiClientId || null;
  }

  // ========================================
  // CONNECTION (Long Polling)
  // ========================================
  connect(userId = null) {
    if (!this.messageBusUrl) {
      this.initialize();
    }

    if (!this.userApiKey) {
      console.warn('DiscourseMessageBus: User API Key não fornecida. A conexão pode falhar. Chame initialize() com a API Key.');
    }

    if (this.polling) {
      return Promise.resolve();
    }

    this.polling = true;
    this.reconnectAttempts = 0;
    
    // Inicia o polling
    this.startPolling(userId);
    
    return Promise.resolve();
  }

  disconnect() {
    this.polling = false;
    if (this.xhr) {
      this.xhr.abort();
      this.xhr = null;
    }
    this.subscriptions.clear();
  }

  startPolling(userId = null) {
    if (!this.polling) return;

    const url = this.buildPollUrl(userId);
    
    this.xhr = new XMLHttpRequest();
    this.xhr.open('POST', url, true);
    
    // Headers para autenticação via User API Key
    this.setHeaders(this.xhr);

    this.xhr.onload = () => {
      if (this.xhr.status === 200) {
        try {
          const response = JSON.parse(this.xhr.responseText);
          if (Array.isArray(response)) {
            response.forEach(message => {
              this.handleMessage(message);
              if (message.message_id) {
                this.lastMessageId = Math.max(this.lastMessageId, message.message_id);
              }
            });
          }
        } catch (e) {
          console.error('Error parsing MessageBus response:', e);
        }
        this.reconnectAttempts = 0;
      } else if (this.xhr.status === 403 || this.xhr.status === 401) {
        console.error('MessageBus authentication failed - verifique sua User API Key');
        this.polling = false;
        return;
      } else {
        this.handlePollError(userId);
      }
      
      // Continua polling
      if (this.polling) {
        setTimeout(() => this.startPolling(userId), 5000);
      }
    };

    this.xhr.onerror = () => {
      this.handlePollError(userId);
    };

    this.xhr.ontimeout = () => {
      // Timeout é normal em long polling, reinicia
      if (this.polling) {
        this.startPolling(userId);
      }
    };

    // Timeout de 25 segundos (padrão Discourse)
    this.xhr.timeout = 25000;

    // Envia dados das subscrições
    const data = this.buildSubscriptionData();
    this.xhr.send(JSON.stringify(data));
  }

  buildPollUrl(userId) {
    let url = `${this.messageBusUrl}poll`;
    const params = [];
    
    // if (userId) {
    //   params.push(`user_id=${userId}`);
    // }
    
    if (this.lastMessageId > 0) {
      params.push(`dlp=t`); // don't long poll flag
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return url;
  }

  buildSubscriptionData() {
    const channels = Array.from(this.subscriptions.keys());
    const data = {};
    
    channels.forEach(channel => {
      data[channel] = this.lastMessageId;
    });

    return data;
  }

  setHeaders(xhr) {
    // User API Key (OBRIGATÓRIO para autenticação)
    if (this.userApiKey) {
      xhr.setRequestHeader('User-Api-Key', this.userApiKey);
    }

    // User API Client ID (opcional)
    if (this.userApiClientId) {
      xhr.setRequestHeader('User-Api-Client-Id', this.userApiClientId);
    }

    // Content-Type
    xhr.setRequestHeader('Content-Type', 'application/json');

    // Discourse-Present (opcional, indica que usuário está ativo)
    xhr.setRequestHeader('Discourse-Present', 'true');
  }

  handlePollError(userId) {
    this.reconnectAttempts++;
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        console.log(`Attempting to reconnect MessageBus (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        if (this.polling) {
          this.startPolling(userId);
        }
      }, this.reconnectDelay);
    } else {
      console.error('Max reconnection attempts reached for MessageBus');
      this.polling = false;
    }
  }

  // ========================================
  // SUBSCRIPTIONS
  // ========================================
  subscribe(channel, callback) {
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, []);
    }
    this.subscriptions.get(channel).push(callback);

    // Se não estiver conectado, conecta agora
    if (!this.polling) {
      this.connect();
    }

    // Retorna função de unsubscribe
    return () => {
      this.unsubscribe(channel, callback);
    };
  }

  unsubscribe(channel, callback) {
    const callbacks = this.subscriptions.get(channel);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        this.subscriptions.delete(channel);
      }
    }
  }

  // ========================================
  // MESSAGE HANDLING
  // ========================================
  handleMessage(message) {
    const { channel, data } = message;

    if (this.subscriptions.has(channel)) {
      const callbacks = this.subscriptions.get(channel);
      callbacks.forEach(callback => {
        try {
          callback(data, message);
        } catch (error) {
          console.error(`Error in MessageBus callback for channel ${channel}:`, error);
        }
      });
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================
  subscribeToTopic(topicId, callback) {
    return this.subscribe(`/topic/${topicId}`, callback);
  }

  subscribeToUser(userId, callback) {
    return this.subscribe(`/user/${userId}`, callback);
  }

  subscribeToNotifications(callback) {
    return this.subscribe('/notification', callback);
  }

  isConnected() {
    return this.polling;
  }

  // Método para atualizar a API Key (útil quando usuário troca de key)
  setApiKey(userApiKey, userApiClientId = null) {
    const wasConnected = this.polling;
    if (wasConnected) {
      this.disconnect();
    }
    
    this.userApiKey = userApiKey;
    this.userApiClientId = userApiClientId;
    
    if (wasConnected) {
      this.connect();
    }
  }
}

export default new DiscourseMessageBus();